import React, { useState } from 'react';
import { Button, DatePicker, Dropdown, Input } from '@salvapp/ui-components';
import { ScreeningAlertFilterObjects } from './helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { ApiScreeningAlertFilterExpressionFieldTypeEnum } from '../../../api';
import { Dayjs } from 'dayjs';

export interface ScreeningAlertFiltersConditionProps {
  expression: ScreeningAlertFiltersCondition;
  screeningAlertFiltersObjects: ScreeningAlertFilterObjects;
  removable?: boolean;
  onConditionChange: (condition: any) => void;
  removeCondition: () => void;
}

export interface ScreeningAlertFiltersCondition {
  target: DropdownOption;
  field: DropdownOption;
  fieldType: string;
  operation: DropdownOption;
  value: any;
}

const renderValueContainer = (type: any, operation: any, value: any, setFieldValue: any, loadValues: () => any) => {
  switch (type) {
    case ApiScreeningAlertFilterExpressionFieldTypeEnum.NUMBER:
      switch (operation) {
        case 'IS_BETWEEN':
          return (
            <>
              <Input
                inputSize='medium'
                type='number'
                value={value ? value[0] : null}
                onChange={(newValue: any) => {
                  if (newValue) {
                    setFieldValue([newValue.target.value, value && value[1] ? value[1] : null]);
                  }
                }}
              />
              <div className='mx-3 align-self-center'>&</div>
              <Input
                inputSize='medium'
                type='number'
                value={value ? value[1] : null}
                onChange={(newValue: any) => {
                  if (newValue) {
                    setFieldValue([value && value[0] ? value[0] : null, newValue.target.value]);
                  }
                }}
              />
            </>
          );
        default:
          return (
            <Input
              inputSize='medium'
              type='number'
              value={value}
              onChange={(value: any) => setFieldValue(value.target.value)}
            />
          );
      }
    case ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING:
      return (
        <Input
          name='value-input'
          inputSize='medium'
          type='text'
          value={value}
          onChange={(value: any) => setFieldValue(value.target.value)}
        />
      );
    case ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST:
      return (
        <Dropdown
          isMulti
          value={value}
          size='medium'
          options={loadValues()}
          onChange={(select: DropdownOption[]) => setFieldValue(select)}
        />
      );
    case ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP:
      switch (operation) {
        case 'IS_BETWEEN':
          return (
            <>
              <DatePicker
                size='medium'
                value={value && value[0] ? value[0] : null}
                showDefaultOptions={false}
                showTime={true}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    setFieldValue([newValue, value && value[1] ? value[1] : undefined]);
                  }
                }}
                isClearable={false}
              />
              <div className='mx-3 align-self-center'>&</div>
              <DatePicker
                size='medium'
                value={value && value[1] ? value[1] : null}
                showDefaultOptions={false}
                showTime={true}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    setFieldValue([value && value[0] ? value[0] : undefined, newValue]);
                  }
                }}
                isClearable={false}
              />
            </>
          );
        default:
          return (
            <DatePicker
              size='medium'
              value={value ? value : null}
              showDefaultOptions={false}
              showTime={true}
              onChange={(value: Dayjs | null) => {
                if (value) {
                  setFieldValue(value);
                }
              }}
              isClearable={false}
            />
          );
      }

    default:
      return (
        <Input
          inputSize='medium'
          type='text'
          value={value}
          onChange={(value: any) => setFieldValue(value.target.value)}
        />
      );
  }
};

export const ScreeningAlertFilterCondition = ({
  expression,
  screeningAlertFiltersObjects,
  removable = false,
  removeCondition,
  onConditionChange,
}: ScreeningAlertFiltersConditionProps): React.JSX.Element | null => {
  const [condition, setCondition] = useState<ScreeningAlertFiltersCondition>({
    target: expression.target,
    field: expression.field,
    fieldType: expression.fieldType,
    operation: expression.operation,
    value: expression.value,
  });

  const validateCondition = (condition: any) => {
    // If current selections for target, field and operation are valid
    const target = screeningAlertFiltersObjects.objects.find((object) => object.value.key === condition.target.key);
    const field = target?.fields.find((field) => field.value.key === condition.field.key);

    const operation = field?.operations.find((operation) => operation.key === condition.operation.key);
    if (target && field && operation) {
      if (condition.value && typeof condition.value === 'object' && condition.value.length > 0) {
        if (operation.key === 'IS_BETWEEN') {
          return condition;
        } else if (field.type === ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
          return { ...condition, value: undefined };
        } else {
          // If value is an array then return only the values that are included in the new type
          return {
            ...condition,
            value: condition.value.filter((value: DropdownOption) =>
              field.options?.some((option) => option.key === value.key)
            ),
          };
        }
      } else {
        return condition;
      }
    } else {
      // If not valid then construct a validCondition
      const validCondition: any = {
        target: condition.target,
      };
      // If there is a valid combination of target and field
      // then return that otherwise return valid one from the mapping
      const newField = target?.fields.find((field) => field.value.key === condition.field.key);
      if (newField) {
        validCondition.field = newField.value;
        validCondition.fieldType = newField.type;
      } else {
        validCondition.field = target?.fields[0]?.value;
        validCondition.fieldType = target?.fields[0]?.type;
      }

      // Based on validCondition target and field check if the operation
      // makes a valid condition. If so then use it, if not return a valid one
      const operations = target?.fields.find((field) => field.value.key === validCondition.field.key)?.operations;
      if (operations?.some((operation) => operation.key === condition.operation.key)) {
        validCondition.operation = condition.operation;
      } else {
        validCondition.operation = operations && operations.length > 0 ? operations[0] : undefined;
      }
      // Will reset the value for validCondition
      validCondition.value = undefined;
      return validCondition;
    }
  };

  const object = screeningAlertFiltersObjects.getObject(condition.target.key);
  if (!object) {
    return <></>;
  }

  if (!condition || !condition.field) return <></>;
  const field = object.getField(condition.field.key);

  if (!field) {
    return <></>;
  }

  const onChange = (field: string, value: DropdownOption) => {
    const updatedCondition = validateCondition({
      ...condition,
      [field]: value,
      fieldType: object.getField(value.key) ? object.getField(value.key)?.type : condition.fieldType,
    });
    setCondition(updatedCondition);
    onConditionChange(updatedCondition);
  };

  return (
    <div className='condition'>
      <Dropdown
        name='target'
        value={condition.target}
        options={screeningAlertFiltersObjects.getObjectOptions()}
        onChange={(select: DropdownOption[]) => onChange('target', select[0])}
        size='medium'
      />
      <Dropdown
        name='field'
        value={condition.field}
        options={object.getFieldOptions()}
        onChange={(select: DropdownOption[]) => onChange('field', select[0])}
        size='medium'
      />
      <Dropdown
        name='operation'
        value={condition.operation}
        options={field.operations}
        onChange={(select: DropdownOption[]) => onChange('operation', select[0])}
        size='medium'
      />
      {renderValueContainer(
        field.type,
        condition.operation.key,
        condition.value,
        (value: DropdownOption) => onChange('value', value),
        () => {
          if (field.options) {
            return field.options;
          }
          return [];
        }
      )}
      {removable && (
        <Button variant='outline-danger' onClick={removeCondition}>
          Remove
        </Button>
      )}
    </div>
  );
};

export default ScreeningAlertFilterCondition;
